import { MandatoryAncillary, OptionalService } from 'common/types'

const filterSegments = (services: OptionalService[]) =>
  services.filter((service) => !service.ancillaryDescription)

const createServiceMasterMap = (items: MandatoryAncillary[]) => {
  const itemsMap: Record<string, MandatoryAncillary> = {}

  for (const item of items) {
    const { group, subgroup, subcode } = item

    const id = `${group}${subgroup || ''}${subcode || ''}`

    if (!itemsMap[id]) {
      itemsMap[id] = item
    }
  }

  return itemsMap
}

const shouldExcludeService = (
  service: OptionalService,
  serviceMasterMap: Record<string, MandatoryAncillary>,
): boolean => {
  const { group, subgroup, subcode } = service
  const key = `${group}${subgroup || ''}${subcode || ''}`

  const master = serviceMasterMap[key]
  if (!master) return false

  const { subcode: masterSubcode, exclude } = master

  return (
    !!masterSubcode &&
    !!subcode &&
    masterSubcode.toLowerCase() === subcode.toLowerCase() &&
    exclude
  )
}

const categorizeServices = (
  services: OptionalService[],
  serviceMasterList: string[],
  serviceMasterMap: Record<string, MandatoryAncillary>,
) => {
  const mandatory: OptionalService[] = []
  const segments: OptionalService[] = []
  const standard: OptionalService[] = []

  for (const service of services) {
    if (shouldExcludeService(service, serviceMasterMap)) {
      continue
    }

    const groupKey = service.group + service.subgroup

    if (serviceMasterList.includes(groupKey)) {
      mandatory.push(service)
    } else if (service.group === 'EQ') {
      segments.push(service)
    } else {
      standard.push(service)
    }
  }

  return { mandatory, segments, standard }
}

const createServiceMasterList = (items: MandatoryAncillary[]): string[] => {
  const ids: string[] = []

  for (const item of items) {
    const { group, subgroup } = item

    const id = `${group}${subgroup || ''}`

    if (!ids.includes(id)) {
      ids.push(id)
    }
  }

  return ids
}

const createServiceKey = (service: OptionalService): string => {
  const { group, subgroup, serviceName } = service
  return `${group}${subgroup || ''}${serviceName || ''}`.replaceAll(/\s/g, '')
}

const deduplicateServices = (
  services: OptionalService[],
): OptionalService[] => {
  const seen = new Set<string>()
  return services.filter((service) => {
    const key = createServiceKey(service)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const splitOptionalServices = (
  services: OptionalService[],
  mandatoryAncillaryList: MandatoryAncillary[],
): {
  distinctMandatoryServiceList: OptionalService[]
  distinctSegmentServiceList: OptionalService[]
  distinctServiceList: OptionalService[]
  all: OptionalService[]
} => {
  const serviceMasterList = createServiceMasterList(mandatoryAncillaryList)
  const serviceMasterMap = createServiceMasterMap(mandatoryAncillaryList)

  const distinctServices = deduplicateServices(services)
  const categorized = categorizeServices(
    distinctServices,
    serviceMasterList,
    serviceMasterMap,
  )

  const segmentsFiltered = filterSegments(categorized.segments)

  return {
    distinctMandatoryServiceList: categorized.mandatory,
    distinctSegmentServiceList: segmentsFiltered,
    distinctServiceList: categorized.standard,
    all: [
      ...categorized.mandatory,
      ...segmentsFiltered,
      ...categorized.standard,
    ],
  }
}
